import React, { useState, useEffect, useCallback, useRef } from 'react';
import PhotoInfoContent from '../components/photos/PhotoInfoContent';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Switch,
    FormControlLabel,
    Typography,
    Grid,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    IconButton,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    AppBar,
    Toolbar,
    Fade,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PublicIcon from '@mui/icons-material/Public';
import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';
import WarningIcon from '@mui/icons-material/Warning';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import { SidebarAd, InFeedAd } from '../components/ads';

import MediaUploadDialog from '../components/MediaUploadDialog';

const API_URL = process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/prod';

// Gesture constants
const SWIPE_THRESHOLD = 50;
const DOUBLE_TAP_DELAY = 300;
const TAP_MOVEMENT_THRESHOLD = 10;

interface Photo {
    photoId: string;
    tripId: string;
    url: string;
    thumbnail: string;
    caption?: string;
    aiCaption?: string;
    aiTags?: string[];
    aiLocationGuess?: string | null;
    visibility: 'private' | 'bffs_travel_partners' | 'friends_family' | 'public';
    uploadedAt: string;
    showCreatorName?: boolean | null;
    tripTitle?: string;
    tripDestination?: string;
    tripDeleted?: boolean;
    share_status?: string;
}

// ============ IMMERSIVE PHOTO VIEWER COMPONENT ============
interface PhotoViewerProps {
    photos: Photo[];
    initialIndex: number;
    open: boolean;
    onClose: () => void;
    onDelete: (photo: Photo) => void;
    onChangeVisibility: (photo: Photo) => void;
    onToggleCreatorName?: (photoId: string, next: boolean, applyToAllInTrip: boolean) => void;
}
const isVideoUrl = (url: string): boolean => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.mov', '.webm', '.ogg', '.avi', '.m4v'];
    const lowerUrl = url.toLowerCase().split('?')[0];
    return videoExtensions.some(ext => lowerUrl.endsWith(ext));
};
const ImmersivePhotoViewer: React.FC<PhotoViewerProps> = ({
    photos,
    initialIndex,
    open,
    onClose,
    onDelete,
    onChangeVisibility,
    onToggleCreatorName,
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [isZoomed, setIsZoomed] = useState(false);
    const [zoomScale, setZoomScale] = useState(1);
    const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
    const [dragOffset, setDragOffset] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [infoExpanded, setInfoExpanded] = useState(false);

    useEffect(() => { setInfoExpanded(false); }, [currentIndex]);
    useEffect(() => { if (!open) setInfoExpanded(false); }, [open]);

    const containerRef = useRef<HTMLDivElement>(null);
    const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
    const lastTapRef = useRef<number>(0);
    const lastTapPositionRef = useRef<{ x: number; y: number } | null>(null);
    const pinchStartDistanceRef = useRef<number | null>(null);
    const initialPinchScaleRef = useRef<number>(1);
    const hasMoved = useRef(false);
    const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const currentPhoto = photos[currentIndex];

    useEffect(() => {
        if (open) {
            setCurrentIndex(initialIndex);
            setIsZoomed(false);
            setZoomScale(1);
            setPanPosition({ x: 0, y: 0 });
            setShowControls(true);
        }
    }, [open, initialIndex]);

    useEffect(() => {
        if (showControls && !isZoomed) {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
            controlsTimeoutRef.current = setTimeout(() => {
                setShowControls(false);
            }, 3000);
        }
        return () => {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
        };
    }, [showControls, isZoomed, currentIndex]);

    const navigatePhoto = useCallback((direction: 'prev' | 'next') => {
        if (isZoomed) return;
        const newIndex = direction === 'next'
            ? Math.min(currentIndex + 1, photos.length - 1)
            : Math.max(currentIndex - 1, 0);
        if (newIndex !== currentIndex) {
            setCurrentIndex(newIndex);
            setShowControls(true);
        }
    }, [currentIndex, photos.length, isZoomed]);

    const handleDoubleTap = useCallback((clientX: number, clientY: number) => {
        if (isZoomed) {
            setIsZoomed(false);
            setZoomScale(1);
            setPanPosition({ x: 0, y: 0 });
        } else {
            const container = containerRef.current;
            if (container) {
                const rect = container.getBoundingClientRect();
                const centerX = rect.width / 2;
                const centerY = rect.height / 2;
                const tapX = clientX - rect.left;
                const tapY = clientY - rect.top;
                const panX = (centerX - tapX) * 1.5;
                const panY = (centerY - tapY) * 1.5;
                setIsZoomed(true);
                setZoomScale(2.5);
                setPanPosition({ x: panX, y: panY });
            } else {
                setIsZoomed(true);
                setZoomScale(2.5);
            }
        }
    }, [isZoomed]);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        setShowControls(true);
        if (e.touches.length === 2) {
            const distance = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            pinchStartDistanceRef.current = distance;
            initialPinchScaleRef.current = zoomScale;
            return;
        }
        const touch = e.touches[0];
        touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
        hasMoved.current = false;
        setIsDragging(true);
    }, [zoomScale]);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (e.touches.length === 2 && pinchStartDistanceRef.current !== null) {
            e.preventDefault();
            const distance = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            const scale = (distance / pinchStartDistanceRef.current) * initialPinchScaleRef.current;
            const clampedScale = Math.max(1, Math.min(4, scale));
            setZoomScale(clampedScale);
            setIsZoomed(clampedScale > 1);
            return;
        }
        if (!touchStartRef.current || !isDragging) return;
        const touch = e.touches[0];
        const deltaX = touch.clientX - touchStartRef.current.x;
        const deltaY = touch.clientY - touchStartRef.current.y;
        if (Math.abs(deltaX) > TAP_MOVEMENT_THRESHOLD || Math.abs(deltaY) > TAP_MOVEMENT_THRESHOLD) {
            hasMoved.current = true;
        }
        if (isZoomed) {
            setPanPosition(prev => ({ x: prev.x + deltaX * 0.8, y: prev.y + deltaY * 0.8 }));
            touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: touchStartRef.current.time };
        } else if (hasMoved.current) {
            setDragOffset(deltaX);
        }
    }, [isDragging, isZoomed]);

    const handleTouchEnd = useCallback(() => {
        pinchStartDistanceRef.current = null;
        if (!touchStartRef.current) {
            setIsDragging(false);
            return;
        }
        const deltaX = dragOffset;
        const deltaTime = Date.now() - touchStartRef.current.time;
        const wasTap = !hasMoved.current && deltaTime < 300;
        if (wasTap) {
            const now = Date.now();
            const tapX = touchStartRef.current.x;
            const tapY = touchStartRef.current.y;
            if (lastTapRef.current && lastTapPositionRef.current) {
                const timeSinceLastTap = now - lastTapRef.current;
                const distanceFromLastTap = Math.hypot(
                    tapX - lastTapPositionRef.current.x,
                    tapY - lastTapPositionRef.current.y
                );
                if (timeSinceLastTap < DOUBLE_TAP_DELAY && distanceFromLastTap < 50) {
                    handleDoubleTap(tapX, tapY);
                    lastTapRef.current = 0;
                    lastTapPositionRef.current = null;
                } else {
                    lastTapRef.current = now;
                    lastTapPositionRef.current = { x: tapX, y: tapY };
                    setShowControls(prev => !prev);
                }
            } else {
                lastTapRef.current = now;
                lastTapPositionRef.current = { x: tapX, y: tapY };
            }
        } else if (!isZoomed) {
            const shouldNavigate = Math.abs(deltaX) > SWIPE_THRESHOLD;
            if (shouldNavigate) {
                if (deltaX < 0) navigatePhoto('next');
                else navigatePhoto('prev');
            }
        }
        setDragOffset(0);
        setIsDragging(false);
        touchStartRef.current = null;
        hasMoved.current = false;
    }, [dragOffset, isZoomed, handleDoubleTap, navigatePhoto]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button !== 0) return;
        const target = e.target as HTMLElement;
        if (target.closest('button, a, [role="button"]')) return;
        setShowControls(true);
        touchStartRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
        hasMoved.current = false;
        setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (!touchStartRef.current || !isDragging) return;
        const deltaX = e.clientX - touchStartRef.current.x;
        const deltaY = e.clientY - touchStartRef.current.y;
        if (Math.abs(deltaX) > TAP_MOVEMENT_THRESHOLD || Math.abs(deltaY) > TAP_MOVEMENT_THRESHOLD) {
            hasMoved.current = true;
        }
        if (isZoomed) {
            setPanPosition(prev => ({ x: prev.x + deltaX * 0.5, y: prev.y + deltaY * 0.5 }));
            touchStartRef.current = { x: e.clientX, y: e.clientY, time: touchStartRef.current.time };
        } else if (hasMoved.current) {
            setDragOffset(deltaX);
        }
    }, [isDragging, isZoomed]);

    const handleMouseUp = useCallback((e: React.MouseEvent) => {
        if (!touchStartRef.current) {
            setIsDragging(false);
            return;
        }
        const deltaX = dragOffset;
        const deltaTime = Date.now() - touchStartRef.current.time;
        const wasTap = !hasMoved.current && deltaTime < 300;
        if (wasTap) {
            const now = Date.now();
            const tapX = e.clientX;
            const tapY = e.clientY;
            if (lastTapRef.current && lastTapPositionRef.current) {
                const timeSinceLastTap = now - lastTapRef.current;
                const distanceFromLastTap = Math.hypot(
                    tapX - lastTapPositionRef.current.x,
                    tapY - lastTapPositionRef.current.y
                );
                if (timeSinceLastTap < DOUBLE_TAP_DELAY && distanceFromLastTap < 50) {
                    handleDoubleTap(tapX, tapY);
                    lastTapRef.current = 0;
                    lastTapPositionRef.current = null;
                } else {
                    lastTapRef.current = now;
                    lastTapPositionRef.current = { x: tapX, y: tapY };
                }
            } else {
                lastTapRef.current = now;
                lastTapPositionRef.current = { x: tapX, y: tapY };
            }
        } else if (!isZoomed) {
            const shouldNavigate = Math.abs(deltaX) > SWIPE_THRESHOLD;
            if (shouldNavigate) {
                if (deltaX < 0) navigatePhoto('next');
                else navigatePhoto('prev');
            }
        }
        setDragOffset(0);
        setIsDragging(false);
        touchStartRef.current = null;
        hasMoved.current = false;
    }, [dragOffset, isZoomed, handleDoubleTap, navigatePhoto]);

    useEffect(() => {
        if (!open) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (isZoomed) {
                    setIsZoomed(false);
                    setZoomScale(1);
                    setPanPosition({ x: 0, y: 0 });
                } else {
                    onClose();
                }
            } else if (e.key === 'ArrowLeft') {
                navigatePhoto('prev');
            } else if (e.key === 'ArrowRight') {
                navigatePhoto('next');
            } else if (e.key === '+' || e.key === '=') {
                if (!isZoomed) {
                    setIsZoomed(true);
                    setZoomScale(2);
                } else if (zoomScale < 4) {
                    setZoomScale(prev => Math.min(4, prev + 0.5));
                }
            } else if (e.key === '-') {
                if (zoomScale > 1) {
                    const newScale = zoomScale - 0.5;
                    if (newScale <= 1) {
                        setIsZoomed(false);
                        setZoomScale(1);
                        setPanPosition({ x: 0, y: 0 });
                    } else {
                        setZoomScale(newScale);
                    }
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, isZoomed, zoomScale, onClose, navigatePhoto]);

    const handleZoomIn = () => {
        if (!isZoomed) {
            setIsZoomed(true);
            setZoomScale(2);
        } else if (zoomScale < 4) {
            setZoomScale(prev => Math.min(4, prev + 0.5));
        }
    };

    const handleZoomOut = () => {
        if (zoomScale > 1) {
            const newScale = zoomScale - 0.5;
            if (newScale <= 1) {
                setIsZoomed(false);
                setZoomScale(1);
                setPanPosition({ x: 0, y: 0 });
            } else {
                setZoomScale(newScale);
            }
        }
    };

    const getVisibilityIcon = (visibility: string) => {
        switch (visibility) {
            case 'public': return <PublicIcon sx={{ fontSize: 14 }} />;
            case 'friends_family': return <GroupIcon sx={{ fontSize: 14 }} />;
            default: return <LockIcon sx={{ fontSize: 14 }} />;
        }
    };

    const getVisibilityLabel = (visibility: string) => {
        switch (visibility) {
            case 'public': return 'Public';
            case 'friends_family': return 'Friends';
            default: return 'Private';
        }
    };

    const getVisibilityColor = (visibility: string) => {
        switch (visibility) {
            case 'public': return { bg: '#E8F5E9', color: '#4CAF50' };
            case 'friends_family': return { bg: '#E3F2FD', color: '#2196F3' };
            default: return { bg: '#FFEBEE', color: '#f44336' };
        }
    };

    if (!open || !currentPhoto) return null;

    const visStyle = getVisibilityColor(currentPhoto.visibility);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            PaperProps={{ sx: { bgcolor: '#000', overflow: 'hidden' } }}
        >
            <Box
                ref={containerRef}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                sx={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    touchAction: 'none',
                    userSelect: 'none',
                    cursor: isDragging ? 'grabbing' : 'default',
                }}
            >
                {/* Blurred background */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -20,
                        left: -20,
                        right: -20,
                        bottom: -20,
                        backgroundImage: `url(${currentPhoto.url})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'blur(30px) brightness(0.4)',
                        transform: 'scale(1.1)',
                    }}
                />

                {/* Main photo */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transform: isZoomed
                            ? `scale(${zoomScale}) translate(${panPosition.x / zoomScale}px, ${panPosition.y / zoomScale}px)`
                            : `translateX(${dragOffset}px)`,
                        transition: isDragging || isZoomed ? 'none' : 'transform 0.3s ease',
                    }}
                >
                    {isVideoUrl(currentPhoto.url) ? (
                        <Box
                            component="video"
                            src={currentPhoto.url}
                            autoPlay
                            controls
                            loop
                            playsInline
                            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            draggable={false}
                        />
                    ) : (
                        <Box
                            component="img"
                            src={currentPhoto.url}
                            alt={currentPhoto.caption || 'Photo'}
                            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            draggable={false}
                        />
                    )}
                </Box>

                {/* Top controls */}
                <Fade in={showControls}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            p: 2,
                            background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                        }}
                    >
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.1)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip
                                icon={getVisibilityIcon(currentPhoto.visibility) as React.ReactElement}
                                label={getVisibilityLabel(currentPhoto.visibility)}
                                size="small"
                                sx={{
                                    bgcolor: visStyle.bg,
                                    color: visStyle.color,
                                    fontWeight: 500,
                                    '& .MuiChip-icon': { color: visStyle.color },
                                }}
                            />
                            <Typography variant="body2" sx={{ color: 'white' }}>
                                {currentIndex + 1} / {photos.length}
                            </Typography>
                        </Box>
                    </Box>
                </Fade>

                {/* Side navigation */}
                {!isZoomed && photos.length > 1 && (
                    <>
                        <Fade in={showControls && currentIndex > 0}>
                            <IconButton
                                onClick={() => navigatePhoto('prev')}
                                sx={{
                                    position: 'absolute',
                                    left: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: 'white',
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    backdropFilter: 'blur(10px)',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                    display: { xs: 'none', md: 'flex' },
                                }}
                            >
                                <ChevronLeftIcon sx={{ fontSize: 32 }} />
                            </IconButton>
                        </Fade>
                        <Fade in={showControls && currentIndex < photos.length - 1}>
                            <IconButton
                                onClick={() => navigatePhoto('next')}
                                sx={{
                                    position: 'absolute',
                                    right: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: 'white',
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    backdropFilter: 'blur(10px)',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                    display: { xs: 'none', md: 'flex' },
                                }}
                            >
                                <ChevronRightIcon sx={{ fontSize: 32 }} />
                            </IconButton>
                        </Fade>
                    </>
                )}

                {/* Zoom controls */}
                <Fade in={showControls}>
                    <Box
                        sx={{
                            position: 'absolute',
                            right: 16,
                            bottom: 200,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        <IconButton
                            onClick={handleZoomIn}
                            disabled={zoomScale >= 4}
                            sx={{
                                color: 'white',
                                bgcolor: 'rgba(0,0,0,0.5)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            <ZoomInIcon />
                        </IconButton>
                        <IconButton
                            onClick={handleZoomOut}
                            disabled={zoomScale <= 1}
                            sx={{
                                color: 'white',
                                bgcolor: 'rgba(0,0,0,0.5)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            <ZoomOutIcon />
                        </IconButton>
                    </Box>
                </Fade>

                {/* Zoom indicator */}
                {isZoomed && (
                    <Fade in>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 80,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                bgcolor: 'rgba(0,0,0,0.7)',
                                borderRadius: '20px',
                                px: 2,
                                py: 1,
                                backdropFilter: 'blur(10px)',
                            }}
                        >
                            <Typography variant="caption" sx={{ color: 'white' }}>
                                {Math.round(zoomScale * 100)}% - Double-tap or ESC to exit
                            </Typography>
                        </Box>
                    </Fade>
                )}

                {/* Bottom info panel */}
                <Fade in={!isZoomed} timeout={0}>
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            p: { xs: 1.5, sm: 3 },
                            background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)',
                            '@media (orientation: landscape) and (max-height: 500px)': {
                                bottom: 16,
                                left: 16,
                                right: 'auto',
                                width: 320,
                                maxHeight: '60vh',
                                p: 0,
                                background: 'rgba(0,0,0,0.75)',
                                backdropFilter: 'blur(20px)',
                                borderRadius: 2,
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                            },
                        }}
                    >
                        {/* Landscape phone: collapsible header (always visible) */}
                        <Box
                            onClick={(e) => { e.stopPropagation(); setInfoExpanded(prev => !prev); }}
                            onTouchStart={(e) => e.stopPropagation()}
                            onTouchMove={(e) => e.stopPropagation()}
                            onTouchEnd={(e) => e.stopPropagation()}
                            sx={{
                                display: 'none',
                                '@media (orientation: landscape) and (max-height: 500px)': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 1.5,
                                    py: 0.75,
                                    cursor: 'pointer',
                                    borderBottom: infoExpanded ? '1px solid rgba(255,255,255,0.15)' : 'none',
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.9rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                    mr: 1,
                                }}
                            >
                                {currentPhoto.tripTitle || currentPhoto.tripDestination || 'Photo'}
                            </Typography>
                            <KeyboardArrowUp
                                sx={{
                                    color: 'white',
                                    fontSize: 20,
                                    transform: infoExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease',
                                }}
                            />
                        </Box>

                        {/* Body: portrait/desktop always; landscape only when expanded */}
                        <Box
                            onTouchStart={(e) => e.stopPropagation()}
                            onTouchMove={(e) => e.stopPropagation()}
                            onTouchEnd={(e) => e.stopPropagation()}
                            sx={{
                                '@media (orientation: landscape) and (max-height: 500px)': {
                                    display: infoExpanded ? 'block' : 'none',
                                    p: 1,
                                },
                            }}
                        >
                            <PhotoInfoContent
                                photo={{ ...currentPhoto, id: currentPhoto.photoId }}
                                tripTitle={currentPhoto.tripTitle || currentPhoto.tripDestination || ''}
                                friends={[]}
                                photos={photos.map(p => ({ ...p, id: p.photoId }))}
                                currentIndex={currentIndex}
                                onEdit={() => { onClose(); onChangeVisibility(currentPhoto); }}
                                onDelete={() => { onClose(); onDelete(currentPhoto); }}
                                onSelectPhoto={setCurrentIndex}
                                showPaginationDots={true}
                                suppressTripTitle={infoExpanded}
                                editLabel="Change Visibility"
                                renderBadges={() => currentPhoto.tripDeleted ? (
                                    <Chip
                                        icon={<WarningIcon />}
                                        label="Orphaned"
                                        color="warning"
                                        size="small"
                                        sx={{ ml: 1, height: 22 }}
                                    />
                                ) : null}
                            />
                        </Box>
                    </Box>
                </Fade>

                {/* Swipe hint */}
                {photos.length > 1 && !isZoomed && (
                    <Fade in={showControls}>
                        <Typography
                            variant="caption"
                            sx={{
                                position: 'absolute',
                                bottom: 180,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                color: 'rgba(255,255,255,0.5)',
                                display: { xs: 'block', md: 'none' },
                            }}
                        >
                            Swipe to see more photos
                        </Typography>
                    </Fade>
                )}
            </Box>
        </Dialog>
    );
};

// ============ MAIN COMPONENT ============
const MyPhotosPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [photos, setPhotos] = useState<Photo[]>([]);
    const { isEnabled } = useFeatureFlags();
    const masterPublicEnabled = isEnabled('master_public');
    const masterFriendsEnabled = isEnabled('master_friends');
    const publicPhotosEnabled = masterPublicEnabled && isEnabled('public_photos');
    const friendsPhotosEnabled = masterFriendsEnabled && isEnabled('friends_photos');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [visibilityDialogOpen, setVisibilityDialogOpen] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
    const [newVisibility, setNewVisibility] = useState<string>('private');
    const [actionLoading, setActionLoading] = useState(false);
    
    // Photo viewer state
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerIndex, setViewerIndex] = useState(0);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

    const fetchPhotos = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const idToken = localStorage.getItem('idToken');
            if (!idToken) {
                navigate('/login');
                return;
            }

            const response = await fetch(`${API_URL}/photos`, {
                headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 401) {
                localStorage.clear();
                navigate('/login');
                return;
            }

            if (response.ok) {
                const data = await response.json();
                setPhotos(data.photos || []);
            } else {
                setError('Failed to load photos');
            }
        } catch (err) {
            console.error('Error fetching photos:', err);
            setError('Failed to load photos');
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        fetchPhotos();
    }, [fetchPhotos]);

    // Photo click - open immersive viewer
    const handlePhotoClick = (photo: Photo, index: number) => {
        setViewerIndex(index);
        setViewerOpen(true);
    };

    const handleDeleteClick = (photo: Photo) => {
        setSelectedPhoto(photo);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedPhoto) return;
        setActionLoading(true);
        try {
            const idToken = localStorage.getItem('idToken');
            const response = await fetch(`${API_URL}/photos/${selectedPhoto.photoId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                setPhotos(prev => prev.filter(p => p.photoId !== selectedPhoto.photoId));
                setDeleteDialogOpen(false);
                setSelectedPhoto(null);
            } else {
                setError('Failed to delete photo');
            }
        } catch (err) {
            console.error('Error deleting photo:', err);
            setError('Failed to delete photo');
        } finally {
            setActionLoading(false);
        }
    };

    const [dialogShowName, setDialogShowName] = useState(false);
    const [dialogApplyAll, setDialogApplyAll] = useState(false);

    const handleVisibilityClick = (photo: Photo) => {
        setSelectedPhoto(photo);
        setNewVisibility(photo.visibility);
        setDialogShowName(photo.showCreatorName !== false);
        setDialogApplyAll(false);
        setVisibilityDialogOpen(true);
    };

    const handleVisibilityConfirm = async () => {
        if (!selectedPhoto) return;
        setActionLoading(true);
        try {
            const idToken = localStorage.getItem('idToken');
            const response = await fetch(`${API_URL}/photos/${selectedPhoto.photoId}/visibility`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ visibility: newVisibility })
            });

            if (response.ok) {
                // Persist name-on-photo setting (public only). Reuses the creator-name endpoint.
                if (newVisibility === 'public') {
                    try {
                        await fetch(`${API_URL}/photos/${selectedPhoto.photoId}/creator-name`, {
                            method: 'PUT',
                            headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ showCreatorName: dialogShowName, applyToAllInTrip: dialogApplyAll })
                        });
                    } catch (e) { /* name save best-effort; visibility already saved */ }
                }
                const tripOfPhoto = selectedPhoto.tripId;
                setPhotos(prev => prev.map(p => {
                    const visMatch = p.photoId === selectedPhoto.photoId;
                    const nameMatch = newVisibility === 'public' && (dialogApplyAll ? p.tripId === tripOfPhoto : p.photoId === selectedPhoto.photoId);
                    if (!visMatch && !nameMatch) return p;
                    return {
                        ...p,
                        ...(visMatch ? { visibility: newVisibility as Photo['visibility'] } : {}),
                        ...(nameMatch ? { showCreatorName: dialogShowName } : {})
                    };
                }));
                setVisibilityDialogOpen(false);
                setSelectedPhoto(null);
            } else {
                setError('Failed to update visibility');
            }
        } catch (err) {
            console.error('Error updating visibility:', err);
            setError('Failed to update visibility');
        } finally {
            setActionLoading(false);
        }
    };

    const getVisibilityIcon = (visibility: string) => {
        switch (visibility) {
            case 'public': return <PublicIcon sx={{ fontSize: 16 }} />;
            case 'friends_family': return <GroupIcon sx={{ fontSize: 16 }} />;
            default: return <LockIcon sx={{ fontSize: 16 }} />;
        }
    };

    const getVisibilityColor = (visibility: string) => {
        switch (visibility) {
            case 'public': return 'success';
            case 'bffs_travel_partners': return 'warning';
            case 'friends_family': return 'info';
            default: return 'default';
        }
    };

    const filteredPhotos = photos.filter(photo => {
        switch (activeTab) {
            case 1: return photo.visibility === 'public';
            case 2: return photo.visibility === 'friends_family' || photo.visibility === 'bffs_travel_partners';
            case 3: return photo.visibility === 'private';
            case 4: return photo.tripDeleted === true;
            default: return true;
        }
    });

    const orphanedCount = photos.filter(p => p.tripDeleted).length;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
            {/* Header */}
            <AppBar position="static" sx={{ bgcolor: '#2D6E6A' }}>
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={() => navigate('/explore')}>
                        <ArrowBackIcon />
                    </IconButton>
                    <PhotoLibraryIcon sx={{ ml: 1, mr: 2 }} />
                    <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
                        My Photos
                    </Typography>
                    {/* Upload button - always visible */}
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setUploadDialogOpen(true)}
                        sx={{ 
                            bgcolor: 'rgba(255,255,255,0.2)', 
                            color: 'white',
                            mr: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                        }}
                    >
                        Upload
                    </Button>
                    <Chip 
                        label={`${photos.length} photos`} 
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                    />
                </Toolbar>
            </AppBar>

            {/* Tabs */}
            <Box sx={{ bgcolor: 'white', boxShadow: 1 }}>
                <Tabs 
                    value={activeTab} 
                    onChange={(_, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ 
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 },
                        '& .Mui-selected': { color: '#2D6E6A' },
                        '& .MuiTabs-indicator': { bgcolor: '#2D6E6A' }
                    }}
                >
                    <Tab label={`All (${photos.length})`} />
                    <Tab label={`Public (${photos.filter(p => p.visibility === 'public').length})`} />
                    <Tab label={`Friends & Family (${photos.filter(p => p.visibility === 'friends_family' || p.visibility === 'bffs_travel_partners').length})`} />
                    <Tab label={`Private (${photos.filter(p => p.visibility === 'private').length})`} />
                    {orphanedCount > 0 && (
                        <Tab 
                            label={`Orphaned (${orphanedCount})`} 
                            icon={<WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                            iconPosition="start"
                        />
                    )}
                </Tabs>
            </Box>

            <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, sm: 3 } }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    <Grid item xs={12} md={9}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                <CircularProgress sx={{ color: '#2D6E6A' }} />
                            </Box>
                        ) : filteredPhotos.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <PhotoLibraryIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">
                                    No photos found
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    {activeTab === 0
                                        ? "You haven't uploaded any photos yet"
                                        : "No photos in this category"
                                    }
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setUploadDialogOpen(true)}
                                    sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' } }}
                                >
                                    Upload Photos
                                </Button>
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                {filteredPhotos.map((photo, index) => (
                                    <React.Fragment key={photo.photoId}>
                                        <Grid item xs={6} sm={4} md={4}>
                                            <Card sx={{ 
                                                height: '100%', 
                                                display: 'flex', 
                                                flexDirection: 'column',
                                                position: 'relative',
                                                '&:hover': { boxShadow: 4 }
                                            }}>
                                                {photo.tripDeleted && (
                                                    <Chip
                                                        icon={<WarningIcon />}
                                                        label="Orphaned"
                                                        color="warning"
                                                        size="small"
                                                        sx={{ 
                                                            position: 'absolute', 
                                                            top: 8, 
                                                            left: 8, 
                                                            zIndex: 1,
                                                            fontSize: '0.7rem'
                                                        }}
                                                    />
                                                )}
                                                {isVideoUrl(photo.url) ? (
                                                    <Box
                                                        component="video"
                                                        src={photo.url}
                                                        muted
                                                        sx={{ width: '100%', height: 160, objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => handlePhotoClick(photo, index)}
                                                    />
                                                ) : (
                                                    <CardMedia
                                                        component="img"
                                                        height="160"
                                                        image={photo.thumbnail || photo.url}
                                                        alt={photo.caption || 'Photo'}
                                                        sx={{ objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => handlePhotoClick(photo, index)}
                                                    />
                                                )}
                                                <CardContent sx={{ flexGrow: 1, py: 1, px: 1.5 }}>
                                                    <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                                        {photo.share_status === 'expired' ? 'Expired shared trip' : (photo.tripTitle || photo.tripDestination || 'Unknown Trip')}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                        <Chip
                                                            icon={getVisibilityIcon(photo.visibility)}
                                                            label={photo.visibility === 'friends_family' ? 'Friends' : photo.visibility}
                                                            size="small"
                                                            color={getVisibilityColor(photo.visibility) as any}
                                                            sx={{ fontSize: '0.7rem', height: 22 }}
                                                        />
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                        {new Date(photo.uploadedAt).toLocaleDateString()}
                                                    </Typography>
                                                    {photo.aiTags && photo.aiTags.length > 0 && (
                                                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, alignItems: 'center', overflow: 'hidden', flexWrap: 'nowrap' }}>
                                                            {photo.aiTags.slice(0, 3).map((tag, idx) => (
                                                                <Box key={idx} sx={{ px: 0.75, py: 0.125, bgcolor: 'rgba(0,180,160,0.1)', color: '#2D6E6A', borderRadius: 1, fontSize: '0.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 90 }}>
                                                                    {tag}
                                                                </Box>
                                                            ))}
                                                            {photo.aiTags.length > 3 && (
                                                                <Box sx={{ px: 0.5, py: 0.125, color: '#2D6E6A', fontSize: '0.7rem', whiteSpace: 'nowrap', fontWeight: 600 }}>
                                                                    +{photo.aiTags.length - 3}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )}
                                                </CardContent>
                                                <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleVisibilityClick(photo)}
                                                        title="Change visibility"
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleDeleteClick(photo)}
                                                        color="error"
                                                        title="Delete photo"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </CardActions>
                                            </Card>
                                        </Grid>

                                        {/* Mobile In-Feed Ad: After every 6 photos */}
                                        {isMobile && (index + 1) % 6 === 0 && index !== filteredPhotos.length - 1 && (
                                            <Grid item xs={12}>
                                                <InFeedAd page="my_photos" index={Math.floor(index / 6)} variant="compact" />
                                            </Grid>
                                        )}
                                    </React.Fragment>
                                ))}
                            </Grid>
                        )}
                    </Grid>
                    {/* Sidebar Ad - Desktop Only */}
                    {!isMobile && (
                        <Grid item md={3}>
                            <SidebarAd page="my_photos" />
                        </Grid>
                    )}
                </Grid>
            </Box>

            {/* Immersive Photo Viewer */}
            <ImmersivePhotoViewer
                photos={filteredPhotos}
                initialIndex={viewerIndex}
                open={viewerOpen}
                onClose={() => setViewerOpen(false)}
                onDelete={handleDeleteClick}
                onChangeVisibility={handleVisibilityClick}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Photo?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this photo? This action cannot be undone.
                    </Typography>
                    {selectedPhoto?.tripDeleted && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            This photo belongs to a deleted trip.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        color="error" 
                        variant="contained"
                        disabled={actionLoading}
                    >
                        {actionLoading ? <CircularProgress size={20} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Visibility Dialog */}
            <Dialog open={visibilityDialogOpen} onClose={() => setVisibilityDialogOpen(false)}>
                <DialogTitle>Change Visibility</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Visibility</InputLabel>
                        <Select
                            value={newVisibility}
                            label="Visibility"
                            onChange={(e) => setNewVisibility(e.target.value)}
                        >
                            <MenuItem value="private">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LockIcon fontSize="small" /> Private
                                </Box>
                            </MenuItem>
                            {friendsPhotosEnabled && <MenuItem value="bffs_travel_partners">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <GroupIcon fontSize="small" sx={{ color: '#E65100' }} /> BFFs/Travel Partners
                                </Box>
                            </MenuItem>}
                            {friendsPhotosEnabled && <MenuItem value="friends_family">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <GroupIcon fontSize="small" /> Friends & Family
                                </Box>
                            </MenuItem>}
                            {publicPhotosEnabled && <MenuItem value="public">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PublicIcon fontSize="small" /> Public
                                </Box>
                            </MenuItem>}
                        </Select>
                    </FormControl>
                    {newVisibility === 'public' && (
                        <Box sx={{ mt: 2 }}>
                            <FormControlLabel
                                control={<Switch checked={dialogShowName} onChange={(e) => setDialogShowName(e.target.checked)} color="primary" />}
                                label={<Typography variant="body2">Show my name on this photo</Typography>}
                            />
                            <FormControlLabel
                                control={<Switch checked={dialogApplyAll} onChange={(e) => setDialogApplyAll(e.target.checked)} size="small" color="primary" />}
                                label={<Typography variant="caption" color="text.secondary">Apply to all photos in this trip</Typography>}
                            />
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                                Only takes effect when "Show my name on public photos" is on in Settings.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVisibilityDialogOpen(false)} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleVisibilityConfirm} 
                        variant="contained"
                        disabled={actionLoading}
                        sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' } }}
                    >
                        {actionLoading ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Media Upload Dialog */}
            <MediaUploadDialog
                open={uploadDialogOpen}
                onClose={() => setUploadDialogOpen(false)}
                onUploadComplete={() => fetchPhotos()}
            />
        </Box>
    );
};

export default MyPhotosPage;
